import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import noDataImage from "./../assets/icons/empty_order.svg";
import "./Orders.css";
import { fetchOrders } from "../slices/orderSlice";
import moment from "moment";
import TopBar from "./../components/TopBar";

const Orders = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("progress");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const [dateFilter, setDateFilter] = useState("all");
  const [searchText, setSearchText] = useState('');

  const applyFilters = (orders) => {
    return orders
      .filter((order) => {
        if (status && order.status !== status) return false;
        if (
          category &&
          !order.products.some((p) => p.product?.category === category)
        )
          return false;
          if (searchText) {
            const matchOrderName = order.products.some(p => p.product && p.product.name.en && p.product.name.en.toLowerCase().includes(searchText));
            const matchBuyerName = order.user.name.toLowerCase().includes(searchText);
            const matchSellerName = order.vendor.name.toLowerCase().includes(searchText);
            if (!matchOrderName && !matchBuyerName && !matchSellerName) return false;
          }
    
        const orderDate = moment(order.createdAt);
        const today = moment();

        switch (dateFilter) {
          case "today":
            return orderDate.isSame(today, "day");
          case "week":
            return orderDate.isSame(today, "week");
          case "month":
            return orderDate.isSame(today, "month");
          case "year":
            return orderDate.isSame(today, "year");
          default:
            return true;
        }
      })
      .sort((a, b) => {
        if (sortConfig.key) {
          const aValue = a[sortConfig.key];
          const bValue = b[sortConfig.key];
          if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
          if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
  };
  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };
  const convertToCSV = (data) => {
    const headers = [
      "Order No",
      "Order Name",
      "Buyer Name",
      "Seller Name",
      "Date",
      "Amount",
      "Order Status",
      "Total Items",
    ];

    const csvRows = [];
    csvRows.push(headers.join(","));

    data.forEach((order, index) => {
      const row = [
        index + 1, // Order No
        order.products
          .map((p) =>
            p.product
              ? p.product.name.en ??
                p.product.name.fr ??
                p.product.name.sq ??
                p.product.name.dr
              : "Unnamed Product"
          )
          .join(", "), // Order Name
        order.user.name, // Buyer Name
        order.vendor.name, // Seller Name
        moment(order.createdAt).format("DD-MM-YY"), // Date
        `€ ${order.totalAmount}`, // Amount
        order.status, // Order Status
        order.products.length, // Total Items
      ];
      csvRows.push(row.join(","));
    });

    return csvRows.join("\n");
  };
  const handleDownloadCSV = () => {
    const csvData = convertToCSV(sortedAndFilteredOrders);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `orders_report_${activeTab}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "asc",
  }); // Default sorting by date ascending

  const ordersFromStore = useSelector((state) => state.orders?.data || []);

  useEffect(() => {
    dispatch(fetchOrders());
  }, [dispatch]);

  const filterOrders = (orders) => {
    return  orders.filter((order) => {
      if (status && order.status !== status) return false;

      if (
        category &&
        !order.products.some(
          (p) => p.product && p.product.category === category
        )
      ) {
        return false;
      }

      return true;
    });
  };

  const progressOrders = filterOrders(
   
    ordersFromStore.filter(
      (order) => order.status === "pending" || order.status === "in transit"
    )
  );

  const completedOrders = filterOrders(
    ordersFromStore.filter(
      (order) =>
        order.status === "completed" ||
        order.status === "returned" ||
        order.status === "cancelled"
    )
  );

  const applySorting = (orders) => {
    if (sortConfig.key) {
      return orders.sort((a, b) => {
        const aValue = getSortValue(a, sortConfig.key);
        const bValue = getSortValue(b, sortConfig.key);
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return orders;
  };

  const getSortValue = (order, key) => {
    switch (key) {
      case "name":
        return order.products
          .map((p) => (p.product ? p.product.name.en : ""))
          .join(", ");
      case "userName":
        return order.user.name;
      case "vendorName":
        return order.vendor.name;
      case "createdAt":
        return order.createdAt;
      case "totalAmount":
        return order.totalAmount;
      case "status":
        return order.status;
      case "totalItems":
        return order.products.length;
      default:
        return "";
    }
  };

  const handleSortingChange = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getPaginatedOrders = (orders) => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return orders.slice(startIndex, endIndex);
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const sortedAndFilteredOrders = applySorting(
    applyFilters(activeTab === "progress" ? progressOrders : completedOrders)
  );

  const totalPages = Math.ceil(sortedAndFilteredOrders.length / ITEMS_PER_PAGE);

  const ordersToDisplay = getPaginatedOrders(sortedAndFilteredOrders);

  const renderOrders = (orders) => (

    <table className="orders-table">
      <thead>
        <tr>
          <th>No.</th>
          <th
            className="header-cell clickable"
            onClick={() => handleSortingChange("name")}
          >
            Order Name{" "}
            {sortConfig.key === "name" &&
              (sortConfig.direction === "asc" ? "↑" : "↓")}
          </th>
          <th
            className="header-cell clickable"
            onClick={() => handleSortingChange("userName")}
          >
            Buyer Name{" "}
            {sortConfig.key === "userName" &&
              (sortConfig.direction === "asc" ? "↑" : "↓")}
          </th>
          <th
            className="header-cell clickable"
            onClick={() => handleSortingChange("vendorName")}
          >
            Seller Name{" "}
            {sortConfig.key === "vendorName" &&
              (sortConfig.direction === "asc" ? "↑" : "↓")}
          </th>
          <th
            className="header-cell clickable"
            onClick={() => handleSortingChange("createdAt")}
          >
            Date{" "}
            {sortConfig.key === "createdAt" &&
              (sortConfig.direction === "asc" ? "↑" : "↓")}
          </th>
          <th
            className="header-cell clickable"
            onClick={() => handleSortingChange("totalAmount")}
          >
            Amount{" "}
            {sortConfig.key === "totalAmount" &&
              (sortConfig.direction === "asc" ? "↑" : "↓")}
          </th>
          <th
            className="header-cell clickable"
            onClick={() => handleSortingChange("status")}
          >
            Order Status{" "}
            {sortConfig.key === "status" &&
              (sortConfig.direction === "asc" ? "↑" : "↓")}
          </th>
          <th
            className="header-cell clickable"
            onClick={() => handleSortingChange("totalItems")}
          >
            Total Items{" "}
            {sortConfig.key === "totalItems" &&
              (sortConfig.direction === "asc" ? "↑" : "↓")}
          </th>
        </tr>
      </thead>
     <tbody>
  {orders.map((order, index) => (
    <tr key={order._id ?? index}>
      <td>{index + 1 + (currentPage - 1) * ITEMS_PER_PAGE}</td>
      <td>
        {order.products
          ?.map((p) =>
            p.product
              ? p.product.name?.en ??
                p.product.name?.fr ??
                p.product.name?.sq ??
                p.product.name?.dr ??
                "Unnamed Product"
              : "Unnamed Product"
          )
          .join(", ")}
      </td>
      <td>{order.user?.name ?? "Unknown User"}</td>
      <td>{order.vendor?.name ?? "Unknown Vendor"}</td>
      <td>{moment(order.createdAt).format("DD-MM-YY")}</td>
      <td>{"€ " + (order.totalAmount ?? 0)}</td>
      <td>{order.status ?? "—"}</td>
      <td>{order.products?.length ?? 0}</td>
    </tr>
  ))}
</tbody>

    </table>
  );

  return (
    <>      <TopBar searchPlaceholder="Search Orders Here" onSearchChange={handleSearchChange}/>
<div className="p-6">
    <div className="orders-container ">
      <div className="buttons-container">
        <button
          className={`btn1 order-btn ${
            activeTab === "progress" ? "active-btn" : "de-btn"
          }`}
          onClick={() => {
            setActiveTab("progress");
            setStatus("");
            setCurrentPage(1);
          }}
        >
          Progress Orders
        </button>
        <button
          className={`btn1 order-btn ${
            activeTab === "completed" ? "active-btn" : "de-btn"
          } ml-4`}
          onClick={() => {
            setActiveTab("completed");
            setStatus("");
            setCurrentPage(1);
          }}
        >
          Completed Orders
        </button>
      </div>
      <div className="dropdown-buttons-container">
        <div className="dropdown">
          <button className="dropdown-button">Date Filter</button>
          <div className="dropdown-content">
            <a href="#" onClick={() => setDateFilter("all")}>
              All Dates
            </a>
            <a href="#" onClick={() => setDateFilter("today")}>
              Today
            </a>
            <a href="#" onClick={() => setDateFilter("week")}>
              This Week
            </a>
            <a href="#" onClick={() => setDateFilter("month")}>
              This Month
            </a>
            <a href="#" onClick={() => setDateFilter("year")}>
              This Year
            </a>
          </div>
        </div>
        <div className="dropdown">
          {" "}
          <button className="dropdown-button">Status</button>
          <div className="dropdown-content">
            {activeTab === "progress" ? (
              <>
                <a href="#" onClick={() => setStatus("")}>
                  All
                </a>
                <a href="#" onClick={() => setStatus("in transit")}>
                  In Transit
                </a>
                <a href="#" onClick={() => setStatus("pending")}>
                  Pending
                </a>
              </>
            ) : (
              <>
                <a href="#" onClick={() => setStatus("")}>
                  All
                </a>
                <a href="#" onClick={() => setStatus("completed")}>
                  Completed
                </a>
                <a href="#" onClick={() => setStatus("returned")}>
                  Returned
                </a>
                <a href="#" onClick={() => setStatus("cancelled")}>
                  Cancelled
                </a>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="orders-content">
        {ordersToDisplay.length > 0 ? (
          renderOrders(ordersToDisplay)
        ) : (
          <div className="empty-orders">
            <img
              src={noDataImage}
              className="empty-orders-icon"
              alt="No Data"
            />
            <h3 className="no-orders-title">No Orders to show right now!</h3>
            <p className="no-orders-text">
              Order section is empty! When someone places the order, its details
              will be shown here.
            </p>
          </div>
        )}
      </div>
      {totalPages > 1 && (
        <div className="text-center pt-5 pagination-controls">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="pl-5 pr-5">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
      <div className="download-report">
        <button className="download-link" onClick={handleDownloadCSV}>
          Download Report
        </button>
      </div>
    </div></div></>
  );
};

export default Orders;
